import React, { useCallback, useEffect, useState } from 'react';
import { Card, Button, Alert } from 'react-bootstrap';
import { useSearchParams, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';

import { RootState } from '../store';
import { DataTable } from '../components/common/DataTable/DataTable';
import { ColumnConfig } from '../components/common/DataTable/types';
import { FilterBar, FilterDefinition } from '../components/common/FilterControls';
import { usePaginatedQuery } from '../hooks/usePaginatedQuery';
import ApplicantDocPreview from '../components/ApplicantDocPreview';
import ApplicantDetailModal from '../components/ApplicantDetailModal';
import {
  getApplicants,
  useGetUsersId,
} from '../api/generated/arrayscapeGamingDashboardAPI';
import type { Applicant, ApplicantDocument, ApplicantsSummary, GetApplicants200, GetApplicantsParams } from '../api/generated/model';

const FETCH_LIMIT = 50;

const fmtDate = (s?: string) => (s ? new Date(s).toLocaleDateString() : '');
const fmtDateTime = (s?: string) => {
  if (!s) return '';
  const d = new Date(s);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`;
};
const fullName = (a: Applicant) => [a.first_name, a.last_name].filter(Boolean).join(' ');

const extLink = (url: string | null | undefined, icon: string) =>
  url ? (
    <a href={url} target="_blank" rel="noreferrer" title={url} onClick={(e) => e.stopPropagation()}>
      <i className={`bi ${icon} fs-5`} />
    </a>
  ) : null;

const filterDefs: FilterDefinition[] = [
  { type: 'text', field: 'search', label: 'Search', icon: 'bi-search', placeholder: 'Name or email…' },
  { type: 'select', field: 'needs_review', label: 'Review', icon: 'bi-exclamation-triangle', options: [{ value: '1', label: 'Needs review only' }] },
];

const ApplicantsTab: React.FC = () => {
  const { user } = useSelector((state: RootState) => state.auth);
  const { data: userData } = useGetUsersId(user?.id || '', { query: { enabled: !!user?.id } });

  // Sort + filter state lives in the URL query params so the view is
  // shareable/bookmarkable and survives reload. All sorting/filtering is
  // server-side (libs/cursor); the URL just drives the query params.
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const sortField = searchParams.get('sort_field') || 'name';
  const sortDir: 'asc' | 'desc' = searchParams.get('sort_direction') === 'desc' ? 'desc' : 'asc';
  const search = searchParams.get('search') || '';
  const needsReview = searchParams.get('needs_review') || '';

  const updateParams = useCallback(
    (updates: Record<string, string | undefined>) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          for (const [k, v] of Object.entries(updates)) {
            if (v) next.set(k, v);
            else next.delete(k);
          }
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  // Always reflect the active sort in the URL — populate the defaults on first
  // load (filters stay absent until the user sets them).
  useEffect(() => {
    if (location.pathname !== '/applicants') return; // don't rewrite URL from another tab
    if (!searchParams.get('sort_field') || !searchParams.get('sort_direction')) {
      updateParams({ sort_field: sortField, sort_direction: sortDir });
    }
  }, [location.pathname, searchParams, updateParams, sortField, sortDir]);

  const [viewerDoc, setViewerDoc] = useState<ApplicantDocument | null>(null);
  const [detailApplicant, setDetailApplicant] = useState<Applicant | null>(null);
  const [showFilterBar, setShowFilterBar] = useState(false);
  const activeFilterCount = (search ? 1 : 0) + (needsReview ? 1 : 0);

  const docLinks = (a: Applicant, type: string) => {
    const ds = (a.documents || [])
      .filter((d) => d.type === type)
      .sort((x, y) => (y.applied_at || '').localeCompare(x.applied_at || '')); // newest first
    if (ds.length === 0) return null;
    return (
      <div className="d-flex flex-column">
        {ds.map((d) => (
          <a
            key={d.id}
            href="#"
            className="text-nowrap"
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); setViewerDoc(d); }}
          >
            {fmtDateTime(d.applied_at)}
          </a>
        ))}
      </div>
    );
  };

  const buildQueryParams = useCallback(
    (cursor: string | undefined) => {
      if (cursor) return { cursor, limit: FETCH_LIMIT };
      return {
        ...(search ? { search } : {}),
        ...(needsReview ? { needs_review: true } : {}),
        sort_field: sortField,
        sort_direction: sortDir,
        limit: FETCH_LIMIT,
      };
    },
    [search, needsReview, sortField, sortDir]
  );

  const {
    allItems,
    totalCount,
    apiSummary,
    isLoading,
    hasMore,
    handleLoadMore,
    reset,
  } = usePaginatedQuery<Applicant, GetApplicants200, ApplicantsSummary>({
    queryKey: 'applicants',
    buildQueryParams,
    queryFn: (params) => getApplicants(params as GetApplicantsParams),
    getRowKey: (a) => a.id,
  });

  const columns: ColumnConfig<Applicant>[] = [
    { key: 'name', label: 'Name', sortable: true, width: 200, renderCell: (a) => (
      <span className="fw-semibold">
        {a.contact_conflict && (
          <i className="bi bi-exclamation-triangle-fill text-warning me-1" title="Contact fields need review" />
        )}
        {fullName(a)}
      </span>
    ) },
    { key: 'email', label: 'Email', sortable: true, width: 240, renderCell: (a) => {
      const emails = a.emails && a.emails.length > 0 ? a.emails : [{ id: 'p', email: a.email, is_primary: true }];
      return (
        <div className="d-flex flex-column">
          {emails.map((e) => (
            <span key={e.id} className="text-truncate">
              {e.email}
              {e.is_primary && emails.length > 1 && <i className="bi bi-star-fill text-warning ms-1" style={{ fontSize: '0.7em' }} title="primary" />}
            </span>
          ))}
        </div>
      );
    } },
    { key: 'phone', label: 'Phone', sortable: true, width: 140, renderCell: (a) => a.phone || '' },
    { key: 'address', label: 'Address', sortable: true, width: 240, renderCell: (a) => a.address || '' },
    { key: 'resume_count', label: 'Resumes', sortable: true, width: 160, renderCell: (a) => docLinks(a, 'resume') },
    { key: 'cover_count', label: 'Cover Letters', sortable: true, width: 160, renderCell: (a) => docLinks(a, 'cover_letter') },
    { key: 'linkedin', label: 'LinkedIn', sortable: true, width: 90, renderCell: (a) => extLink(a.linkedin, 'bi-linkedin') },
    { key: 'github', label: 'GitHub', sortable: true, width: 90, renderCell: (a) => extLink(a.github, 'bi-github') },
    { key: 'last_applied_at', label: 'Last Applied', sortable: true, width: 130, renderCell: (a) => fmtDate(a.last_applied_at) },
    { key: 'first_applied_at', label: 'First Applied', sortable: true, width: 130, renderCell: (a) => fmtDate(a.first_applied_at) },
  ];

  return (
      <>
      <style>{`
        .summary-cards-row { display: flex !important; flex-wrap: wrap !important; gap: 1rem !important; margin-bottom: 1rem !important; }
        .summary-card-col { flex: 0 0 175px !important; width: 175px !important; }
        .summary-card { transition: all 0.2s ease; border: 2px solid transparent !important; }
        .summary-card:hover { transform: translateY(-2px); box-shadow: 0 4px 12px rgba(0,0,0,0.15) !important; }
        .summary-card-active { border: 2px solid #0d6efd !important; background-color: rgba(13, 110, 253, 0.05); }
      `}</style>
      <div className="summary-cards-row">
        <div className="summary-card-col">
          <Card className="h-100">
            <Card.Body className="text-center p-2">
              <div className="mb-2"><i className="bi bi-file-earmark-person fs-1 text-primary"></i></div>
              <h3 className="mb-1">{apiSummary?.total_count ?? totalCount ?? allItems.length}</h3>
              <p className="text-muted mb-0 small">Applicants</p>
            </Card.Body>
          </Card>
        </div>
        <div className="summary-card-col">
          <Card
            className={`h-100 summary-card ${needsReview ? 'summary-card-active' : ''}`}
            onClick={() => updateParams({ needs_review: needsReview ? undefined : '1' })}
            style={{ cursor: 'pointer' }}
          >
            <Card.Body className="text-center p-2">
              <div className="mb-2"><i className="bi bi-exclamation-triangle fs-1 text-warning"></i></div>
              <h3 className="mb-1">{apiSummary?.needs_review_count ?? 0}</h3>
              <p className="text-muted mb-0 small">Need Review</p>
            </Card.Body>
          </Card>
        </div>
      </div>
      <Card className="d-flex flex-column flex-grow-1" style={{ minHeight: '240px', overflow: 'hidden' }}>
        <Card.Header className="bg-primary text-white flex-shrink-0">
          <div className="d-flex justify-content-between align-items-center m-1 flex-wrap gap-2">
            <div className="d-flex align-items-center gap-2 flex-wrap">
              <div className="bg-header-control rounded px-2 py-1 d-flex align-items-center gap-2">
                <span className="text-primary fw-semibold">
                  <i className="bi bi-file-earmark-person me-1"></i>
                  Applicants
                </span>
                <div className="border-end border-primary mx-2" style={{ height: '24px' }}></div>
                <Button
                  variant={activeFilterCount > 0 ? 'primary' : 'outline-primary'}
                  size="sm"
                  onClick={() => setShowFilterBar(!showFilterBar)}
                >
                  <i className="bi bi-funnel me-1"></i>
                  Filters
                  {activeFilterCount > 0 && (
                    <span className="badge bg-light text-primary ms-1">{activeFilterCount}</span>
                  )}
                </Button>
              </div>
            </div>
            <div className="d-flex align-items-center gap-3 flex-wrap">
              <div className="rounded px-3 py-1">
                <span className="text-white">
                  {allItems.length > 0 ? (
                    <>{totalCount ?? allItems.length} total</>
                  ) : (
                    'No applicants found'
                  )}
                </span>
              </div>
            </div>
          </div>
          {showFilterBar && (
            <FilterBar
              filters={{ search, needs_review: needsReview }}
              filterDefs={filterDefs}
              onFilterChange={(f, v) => updateParams({ [f]: v || undefined })}
              onClearFilters={() => updateParams({ search: undefined, needs_review: undefined })}
            />
          )}
        </Card.Header>
        <Card.Body className="flex-grow-1 overflow-hidden p-0" style={{ minHeight: 0 }}>
          <DataTable<Applicant>
            data={allItems}
            columns={columns}
            getRowKey={(a) => a.id}
            sortConfig={{ field: sortField, direction: sortDir }}
            onSortChange={(field, dir) => updateParams({ sort_field: field, sort_direction: dir })}
            onRowClick={(a) => setDetailApplicant(a)}
            pagination={{ hasMore, limit: FETCH_LIMIT }}
            onLoadMore={handleLoadMore}
            isLoading={isLoading}
            preferencesKey="applicants"
            userId={user?.id}
            userPreferences={userData?.preferences}
            maxHeight="100%"
            enableStickyHeader
            enableColumnReorder
            enableColumnResize
            enableColumnVisibility
            emptyState={<Alert variant="secondary" className="m-3">No applicants found.</Alert>}
          />
        </Card.Body>
      </Card>
      <ApplicantDocPreview doc={viewerDoc} onClose={() => setViewerDoc(null)} />
      <ApplicantDetailModal show={!!detailApplicant} applicant={detailApplicant} onHide={() => setDetailApplicant(null)} onChanged={reset} onDeleted={() => { setDetailApplicant(null); reset(); }} />
      </>
  );
};

export default ApplicantsTab;
